import { Card, List } from 'antd';
import { useTranslation } from 'react-i18next';
import get from 'lodash/get';
import Pagination from 'components/molecules/Pagination';
import './index.scss';
import { Fragment } from 'react';

const ListView = ({
  columns,
  dataSource,
  pagination = false,
  serverSide = false,
  /* False when the Table is drawing the pager itself, in the footer row it
     shares with the caller's own controls. The config still arrives, because
     the slicing below depends on it. */
  renderPagination = true,
  onPageChange,
  localePath = '',
  rowKey,
  loading,
  showListGird = true,
  header,
  bordered,
  ...props
}) => {
  const { t } = useTranslation(localePath);

  /* The table's widest column is the one carrying the record's identity - the
     part name - so on a card it spans the full width instead of being folded
     into half of it. Tables that size no column keep an even grid. */
  const leadIndex = columns.reduce(
    (widest, col, i) => (parseFloat(col.width) > (parseFloat(columns[widest]?.width) || 0) ? i : widest),
    -1,
  );

  const renderItem = (record, index) => {
    return (
      <Card bordered>
        <div className={showListGird ? 'list-view-wrapper list-grid-wrapper' : 'list-view-wrapper'}>
          {columns.map((col, colIndex) => {
            const value = get(record, col.dataIndex);
            return (
              <Fragment key={`${col.dataIndex}-${record[rowKey]}`}>
                {col.showAsTitle ? (
                  <div className="list-title">{col.render(value, record, index)}</div>
                ) : (
                  /* Label and value are separate elements so the card can set
                     them apart - a muted caption over a readable value -
                     rather than running them together as "label: value". */
                  <div className={colIndex === leadIndex ? 'list-cell list-cell--lead' : 'list-cell'}>
                    <span className="list-cell__label">{t(col.title)}</span>
                    <span className="list-cell__value">{col.render(value, record, index)}</span>
                  </div>
                )}
              </Fragment>
            );
          })}
        </div>
      </Card>
    );
  };
  /* antd's List always slices the array it is handed, which is right when the
     screen holds every record and wrong when it holds only the page the API
     just returned - there, slicing a 20-record array by `(page - 1) * 20` left
     page two empty. So the pager is rendered separately and the slicing is done
     here, only for the case that calls for it. */
  const { current = 1, pageSize } = pagination || {};
  const pagedDataSource =
    pagination && !serverSide ? dataSource.slice((current - 1) * pageSize, current * pageSize) : dataSource;

  return (
    <>
      <List
        grid={{ gutter: 16, column: 1 }}
        dataSource={pagedDataSource}
        pagination={false}
        renderItem={(item, index) => <List.Item>{renderItem(item, index)}</List.Item>}
        rowKey={rowKey}
        loading={loading}
        header={props.header && <div className="list-title">{props.header}</div>}
        /* antd's Table takes a render function here and its List takes a node,
           so accept either and hand List the node. */
        footer={typeof props.footer === 'function' ? props.footer(pagedDataSource) : props.footer}
        bordered={bordered}
        className="custom-table-list-wrapper"
      />
      {pagination && renderPagination && (
        <Pagination
          current={current}
          pageSize={pageSize}
          total={pagination.total}
          showSizeChanger={pagination.showSizeChanger}
          hideOnSinglePage={pagination.hideOnSinglePage}
          onChange={onPageChange}
        />
      )}
    </>
  );
};

export default ListView;
